
import React, { useState } from 'react';
import { supabase } from '../supabase';
import { UserRole } from '../types';
import { Building2, UserCircle, Loader2, Mail, Lock, ArrowRight, AlertCircle, Info, Key, Check } from 'lucide-react';

import LegalFooter from './LegalFooter';
import GymOnboarding from './GymOnboarding';

const Login: React.FC<{ onLogin: () => void }> = () => {
  const [activeTab, setActiveTab] = useState<UserRole>(UserRole.CLIENT);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ 
        email: email.toLowerCase().trim(), 
        password 
      });

      if (authError) {
        if (authError.message.includes("Invalid login credentials")) {
          throw new Error(activeTab === UserRole.CLIENT 
            ? "PIN o correo incorrectos." 
            : "Credenciales de staff incorrectas.");
        }
        throw authError;
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError("Ingresa tu correo para restablecer la contraseña.");
      return;
    }
    setResetLoading(true);
    setError(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) throw resetError;
      setResetSuccess(true);
      setTimeout(() => setResetSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-dark-bg relative overflow-hidden">
      {/* Background Accents */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand/10 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand/5 rounded-full blur-[120px]"></div>

      <div className="w-full max-w-md relative z-10">
        <div className="glass-card overflow-hidden border-white/5">
          <div className="p-6 sm:p-10 text-center border-b border-white/5 relative">
            <div className="inline-flex items-center justify-center w-28 h-28 sm:w-40 sm:h-40 bg-brand/10 rounded-full mb-6 p-5 border border-brand/20">
              <img 
                src="https://hshqshiznfwasvgtcjaj.supabase.co/storage/v1/object/sign/logo/logo.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9iYzQ1ODVmNy01ZWVlLTQ0MzctYjk3Mi1lNGYyYzQ0ZjM4ZmUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvL2xvZ28ucG5nIiwiaWF0IjoxNzcxNTU0ODk2LCJleHAiOjQ4OTM2MTg4OTZ9.fxSBsODmtEQ_0T3sewE7hBTPcKBjlsvo3qJQPdxZCOU" 
                alt="Logo" 
                className="w-full h-full object-contain brightness-110"
                referrerPolicy="no-referrer"
              />
            </div>
            <h1 className="text-3xl sm:text-4xl font-display uppercase tracking-tighter text-white mb-1">GymBrotherhood</h1>
            <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-bold">Elite Performance Network</p>
            <p className="text-brand text-[11px] font-display uppercase tracking-[0.2em] mt-2 italic opacity-80">be a bro</p>
          </div>

          <div className="flex p-1 bg-white/5 border-b border-white/5">
            <button 
              onClick={() => { setActiveTab(UserRole.CLIENT); setError(null); }}
              className={`flex-1 py-4 text-[10px] font-display uppercase tracking-widest transition-all ${activeTab === UserRole.CLIENT ? 'bg-brand text-black' : 'text-white/40 hover:text-white/60'}`}
            >
              Socio
            </button>
            <button 
              onClick={() => { setActiveTab(UserRole.GYM); setError(null); }}
              className={`flex-1 py-4 text-[10px] font-display uppercase tracking-widest transition-all ${activeTab === UserRole.GYM ? 'bg-brand text-black' : 'text-white/40 hover:text-white/60'}`}
            >
              Gimnasio
            </button>
          </div>

          <div className="p-6 sm:p-10">
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] ml-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  <input 
                    type="email" 
                    placeholder="atleta@elite.com" 
                    className="luxury-input w-full pl-12"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] ml-1">
                  {activeTab === UserRole.CLIENT ? 'PIN de Acceso' : 'Password'}
                </label>
                <div className="relative">
                  {activeTab === UserRole.CLIENT ? (
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  ) : (
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  )}
                  <input 
                    type="password" 
                    placeholder={activeTab === UserRole.CLIENT ? "••••••" : "••••••••"} 
                    className="luxury-input w-full pl-12"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                {activeTab === UserRole.GYM && (
                  <div className="flex justify-end">
                    <button 
                      type="button"
                      onClick={handleResetPassword}
                      disabled={resetLoading}
                      className="text-[9px] text-brand hover:underline font-bold uppercase tracking-widest disabled:opacity-50"
                    >
                      {resetLoading ? 'Enviando...' : '¿Olvidaste tu contraseña?'}
                    </button>
                  </div>
                )}
              </div>

              {resetSuccess && (
                <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
                  <Check size={16} className="shrink-0" />
                  <span>Se ha enviado un enlace a tu correo.</span>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-[10px] font-bold uppercase tracking-wider">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                className="brutal-btn w-full flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : 'Acceder'}
                {!loading && <ArrowRight size={18} />}
              </button>

              <div className="flex items-start gap-3 text-[9px] text-white/30 leading-relaxed bg-white/5 p-4 rounded-xl border border-white/5">
                <Info size={14} className="shrink-0 mt-0.5 text-brand/50" />
                <div className="flex-1">
                  <p className="uppercase tracking-wider mb-2">
                    {activeTab === UserRole.CLIENT 
                      ? 'Usa el correo y PIN facilitados en tu gimnasio base.' 
                      : 'Acceso exclusivo para personal administrativo.'}
                  </p>
                  {activeTab === UserRole.GYM && (
                    <button 
                      type="button"
                      onClick={() => setShowOnboarding(true)}
                      className="text-brand hover:underline font-bold uppercase tracking-widest"
                    >
                      ¿Quieres formar parte de la hermandad?
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
        <LegalFooter />
      </div>

      {showOnboarding && <GymOnboarding onClose={() => setShowOnboarding(false)} />}
    </div>
  );
};

export default Login;
